/**
 * PLM Contamination Check — the screen helpers (prototype
 * PLM-Contamination-Check-v2, 2026-10-16). The counting and the statuses are the
 * server's (PlmStationRules); these only turn a Period choice into dates.
 */

/** The Period presets of the prototype's filter row. */
export type ContamPeriod = 'tm' | 'lm' | 'tq' | 'lq' | 'ytd' | 'custom';

export const CONTAM_PERIODS: { value: ContamPeriod; label: string }[] = [
  { value: 'tm', label: 'This month' },
  { value: 'lm', label: 'Last month' },
  { value: 'tq', label: 'This quarter' },
  { value: 'lq', label: 'Last quarter' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'custom', label: 'Custom…' },
];

/** The prototype opens on This quarter. */
export const DEFAULT_CONTAM_PERIOD: ContamPeriod = 'tq';

const pad = (n: number) => String(n).padStart(2, '0');
/** A date as the yyyy-mm-dd a date input holds (local calendar day, no time zone). */
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The From / To of a Period preset, as the prototype's `setPeriod` computes
 * them. "This …" periods end today; "Last …" periods end on their last day.
 * Custom keeps whatever dates were typed, so it returns null.
 */
export function contamPeriodRange(period: ContamPeriod, today: Date): { from: string; to: string } | null {
  const y = today.getFullYear();
  const m = today.getMonth();
  const q = Math.floor(m / 3);
  switch (period) {
    case 'tm': return { from: isoDay(new Date(y, m, 1)), to: isoDay(today) };
    case 'lm': return { from: isoDay(new Date(y, m - 1, 1)), to: isoDay(new Date(y, m, 0)) };
    case 'tq': return { from: isoDay(new Date(y, q * 3, 1)), to: isoDay(today) };
    case 'lq': return { from: isoDay(new Date(y, q * 3 - 3, 1)), to: isoDay(new Date(y, q * 3, 0)) };
    case 'ytd': return { from: isoDay(new Date(y, 0, 1)), to: isoDay(today) };
    default: return null;
  }
}

/**
 * The Station / Analyst cascade of the filter (owner request 2026-10-16, "like
 * Sample Registration"): picking an analyst narrows Station to the stations that
 * analyst has read at, and picking a station narrows Analyst to who has read
 * there. The current pick always stays in its own list, so it is never orphaned.
 */
export function contamCascade(
  pairs: readonly { station: string; analyst: string }[],
  stations: readonly string[],
  analysts: readonly string[],
  station: string,
  analyst: string,
): { stations: string[]; analysts: string[] } {
  const eq = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
  const keep = (list: readonly string[], allowed: Set<string> | null, pick: string) =>
    list.filter(v => !allowed || [...allowed].some(a => eq(a, v)) || (pick && eq(v, pick)));
  const stationsOf = analyst ? new Set(pairs.filter(p => eq(p.analyst, analyst)).map(p => p.station)) : null;
  const analystsOf = station ? new Set(pairs.filter(p => eq(p.station, station)).map(p => p.analyst)) : null;
  return { stations: keep(stations, stationsOf, station), analysts: keep(analysts, analystsOf, analyst) };
}
